import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import SupportChat from './components/SupportChat';
import TermsUpdateBanner from './components/TermsUpdateBanner';

// Pages
import HomePage from './pages/HomePage';
import BrowsePage from './pages/BrowsePage';
import ListingDetailPage from './pages/ListingDetailPage';
import CreateListingPage from './pages/CreateListingPage';
import DashboardPage from './pages/DashboardPage';
import MessagesPage from './pages/MessagesPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SellerSettingsPage from './pages/SellerSettingsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminSupportPage from './pages/AdminSupportPage';
import AdminUsersPage from './pages/AdminUsersPage';
import PrivacyPage from './pages/PrivacyPage';
import ProfilePage from './pages/ProfilePage';
import TradesBrowsePage from './pages/TradesBrowsePage';
import UserProfilePage from './pages/UserProfilePage';
import AdminTradiesPage from './pages/AdminTradiesPage';
import AdminBroadcastPage from './pages/AdminBroadcastPage';
import CreatorFeedPage from './pages/CreatorFeedPage';
import CreateCreatorPostPage from './pages/CreateCreatorPostPage';
import MyVideosPage from './pages/MyVideosPage';
import AboutPage from './pages/AboutPage';
import AdminPromotionsPage from './pages/AdminPromotionsPage';
import GetFeaturedPage from './pages/GetFeaturedPage';
import AdminMerchandisePage from './pages/AdminMerchandisePage';
import MerchandisePage from './pages/MerchandisePage';
import MerchCheckoutPage from './pages/MerchCheckoutPage';
import TermsPage from './pages/TermsPage';
import TrialSetupPage from './pages/TrialSetupPage';

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Routes>
        {/* Auth routes without header */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Public pages accessible without login */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        
        {/* Main routes with header */}
        <Route path="/*" element={
            <>
              <Header />
              <Routes>
                <Route index element={<HomePage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="browse" element={<BrowsePage />} />
                <Route path="workshops" element={<Navigate to="/browse?listing_type=workshop" replace />} />
                <Route path="listing/:id" element={<ListingDetailPage />} />
                <Route path="create-listing" element={<CreateListingPage />} />
                <Route path="edit-listing/:id" element={<CreateListingPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="messages/:conversationId" element={<MessagesPage />} />
                <Route path="subscription" element={<SubscriptionPage />} />
                <Route path="subscription/success" element={<SubscriptionPage />} />
                <Route path="subscription/trial-success" element={<TrialSetupPage />} />
                <Route path="setup-trial" element={<TrialSetupPage />} />
                <Route path="seller/settings" element={<SellerSettingsPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="profile/:userId" element={<UserProfilePage />} />
                <Route path="trades" element={<TradesBrowsePage />} />
                <Route path="creator" element={<CreatorFeedPage />} />
                <Route path="creator/post" element={<CreateCreatorPostPage />} />
                <Route path="creator/my-videos" element={<MyVideosPage />} />
                <Route path="admin/support" element={<AdminSupportPage />} />
                <Route path="admin/users" element={<AdminUsersPage />} />
                <Route path="admin/tradies" element={<AdminTradiesPage />} />
                <Route path="admin/broadcast" element={<AdminBroadcastPage />} />
                <Route path="admin/promotions" element={<AdminPromotionsPage />} />
                <Route path="admin/merchandise" element={<AdminMerchandisePage />} />
                <Route path="get-featured" element={<GetFeaturedPage />} />
                {/* Merch store hidden — re-enable when a new supplier is sourced. Admin route kept for prep work. */}
                <Route path="merch" element={<Navigate to="/" replace />} />
                <Route path="merch/checkout/:itemId" element={<Navigate to="/" replace />} />
                {/* Legacy coming-soon route — site is now live */}
                <Route path="coming-soon" element={<Navigate to="/" replace />} />
              </Routes>
            </>
        } />
      </Routes>
      <SupportChat />
      <TermsUpdateBanner />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
